import { stackBlur } from './stackBlur';

const scaledMaskCache = new WeakMap<HTMLCanvasElement, Map<string, HTMLCanvasElement>>();

export function createCanvas(width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

export function cloneMask(mask: HTMLCanvasElement): HTMLCanvasElement {
  const out = createCanvas(mask.width, mask.height);
  const ctx = out.getContext('2d', { willReadFrequently: true });
  if (ctx) ctx.drawImage(mask, 0, 0, out.width, out.height);
  return out;
}

export function scaleMask(
  mask: HTMLCanvasElement,
  width: number,
  height: number
): HTMLCanvasElement {
  if (mask.width === width && mask.height === height) return mask;
  const key = `${width}x${height}`;
  const cachedBySize = scaledMaskCache.get(mask);
  const cached = cachedBySize?.get(key);
  if (cached) return cached;
  const out = createCanvas(width, height);
  const ctx = out.getContext('2d');
  if (ctx) ctx.drawImage(mask, 0, 0, width, height);
  if (cachedBySize) {
    cachedBySize.set(key, out);
  } else {
    scaledMaskCache.set(mask, new Map([[key, out]]));
  }
  return out;
}

export function combineMasks(
  width: number,
  height: number,
  masks: readonly (HTMLCanvasElement | null | undefined)[]
): HTMLCanvasElement {
  const out = createCanvas(width, height);
  const ctx = out.getContext('2d');
  if (!ctx) return out;
  for (const mask of masks) {
    if (mask) ctx.drawImage(mask, 0, 0, width, height);
  }
  return out;
}

export function subtractMasks(
  mask: HTMLCanvasElement,
  cutouts: readonly (HTMLCanvasElement | null | undefined)[]
): HTMLCanvasElement {
  const out = cloneMask(mask);
  const ctx = out.getContext('2d');
  if (!ctx) return out;
  ctx.globalCompositeOperation = 'destination-out';
  for (const cutout of cutouts) {
    if (cutout) ctx.drawImage(cutout, 0, 0, out.width, out.height);
  }
  ctx.globalCompositeOperation = 'source-over';
  return out;
}

export function intersectMasks(
  mask: HTMLCanvasElement,
  clips: readonly (HTMLCanvasElement | null | undefined)[]
): HTMLCanvasElement {
  const out = cloneMask(mask);
  const ctx = out.getContext('2d');
  if (!ctx) return out;
  ctx.globalCompositeOperation = 'destination-in';
  for (const clip of clips) {
    if (clip) ctx.drawImage(clip, 0, 0, out.width, out.height);
  }
  ctx.globalCompositeOperation = 'source-over';
  return out;
}

export function softenMask(mask: HTMLCanvasElement, radiusPx: number): HTMLCanvasElement {
  const out = cloneMask(mask);
  const ctx = out.getContext('2d', { willReadFrequently: true });
  if (!ctx || radiusPx <= 0) return out;
  stackBlur(ctx, 0, 0, out.width, out.height, radiusPx);
  return out;
}

export function thresholdMask(
  mask: HTMLCanvasElement,
  alphaThreshold = 32
): HTMLCanvasElement {
  const out = createCanvas(mask.width, mask.height);
  const src = mask.getContext('2d', { willReadFrequently: true });
  const dst = out.getContext('2d');
  if (!src || !dst) return out;
  const image = src.getImageData(0, 0, mask.width, mask.height);
  const data = image.data;
  for (let i = 3; i < data.length; i += 4) {
    data[i] = (data[i] ?? 0) >= alphaThreshold ? 255 : 0;
  }
  dst.putImageData(image, 0, 0);
  return out;
}

export function growMask(mask: HTMLCanvasElement, radiusPx: number): HTMLCanvasElement {
  if (radiusPx <= 0) return cloneMask(mask);
  return thresholdMask(softenMask(mask, radiusPx), 8);
}

// Erode a mask inward by ~radiusPx. Inverse of growMask: blur spreads the edge
// into a ramp, then a HIGH alpha threshold keeps only the deeply-interior pixels
// (the ones still near-opaque after the blur), pulling the boundary inward.
// Used when a mask built to *protect* a region (deliberately grown) must be
// reused to *paint* it, where any over-reach leaks colour onto neighbours.
export function shrinkMask(mask: HTMLCanvasElement, radiusPx: number): HTMLCanvasElement {
  if (radiusPx <= 0) return cloneMask(mask);
  return thresholdMask(softenMask(mask, radiusPx), 230);
}

export function polishMask(
  mask: HTMLCanvasElement,
  options: { growPx?: number; featherPx?: number; threshold?: number } = {}
): HTMLCanvasElement {
  let out = options.growPx ? growMask(mask, options.growPx) : cloneMask(mask);
  if (options.threshold !== undefined) out = thresholdMask(out, options.threshold);
  if (options.featherPx) out = softenMask(out, options.featherPx);
  return out;
}

export function maskAlphaCoverage(mask: HTMLCanvasElement, alphaThreshold = 8): number {
  const ctx = mask.getContext('2d', { willReadFrequently: true });
  if (!ctx || mask.width === 0 || mask.height === 0) return 0;
  const data = ctx.getImageData(0, 0, mask.width, mask.height).data;
  let covered = 0;
  for (let i = 3; i < data.length; i += 4) {
    if ((data[i] ?? 0) >= alphaThreshold) covered++;
  }
  return covered / (mask.width * mask.height);
}

export function maskOverlapRatio(
  candidate: HTMLCanvasElement,
  target: HTMLCanvasElement,
  alphaThreshold = 8
): number {
  if (candidate.width !== target.width || candidate.height !== target.height) return 0;
  const cctx = candidate.getContext('2d', { willReadFrequently: true });
  const tctx = target.getContext('2d', { willReadFrequently: true });
  if (!cctx || !tctx || candidate.width === 0 || candidate.height === 0) return 0;
  const c = cctx.getImageData(0, 0, candidate.width, candidate.height).data;
  const t = tctx.getImageData(0, 0, target.width, target.height).data;
  let targetCovered = 0;
  let overlap = 0;
  for (let i = 3; i < t.length; i += 4) {
    if ((t[i] ?? 0) >= alphaThreshold) {
      targetCovered++;
      if ((c[i] ?? 0) >= alphaThreshold) overlap++;
    }
  }
  return targetCovered ? overlap / targetCovered : 0;
}

export function useMaskWhenCovered(
  candidate: HTMLCanvasElement,
  fallback: HTMLCanvasElement,
  minCoverage: number,
  maxCoverage = 1
): HTMLCanvasElement {
  const coverage = maskAlphaCoverage(candidate);
  return coverage >= minCoverage && coverage <= maxCoverage ? candidate : cloneMask(fallback);
}
